import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';
import { SnackbarService } from '../../../../services/snackbar.service';
import { SnackbarAlertStatus } from '../../../../utils';
import {
  ManagerPickerModalComponent,
  ManagerPickerResult,
} from '../../components/manager-picker-modal/manager-picker-modal.component';
import { RemoveManagerModalComponent } from '../../components/remove-manager-modal/remove-manager-modal.component';
import { SalesTeamManagementApi } from '../../sales-team-management.api';
import {
  ReportingLine,
  SalesTeamFocus,
  SalesTeamIndexEntry,
  SalesTeamLevel,
  SalesTeamNode,
  SalesTeamRow,
  SalesTeamSummary,
} from '../../sales-team-management.model';
import {
  EMPTY_SALES_TEAM_SUMMARY,
  expandableIds,
  flattenSalesTeam,
  focusBranch,
  indexSalesTeam,
  isRegionalSalesManager,
  levelTone,
  managerCandidates,
  managerLevelFor,
  parseReportingLineErrors,
  salesTeamLevel,
  summarizeSalesTeam,
} from '../../sales-team-management.util';

type LoadState = 'loading' | 'ready' | 'forbidden' | 'error';

interface SummaryStat {
  label: string;
  count: number;
  /** Colour key shared with the avatars and the progress bar. */
  tone: string;
}

/** A slice of the "placed in the reporting chain" bar. */
interface ProgressSegment {
  tone: string;
  label: string;
  percent: number;
}

/** The Unassigned group under Admin expands and collapses like a person, keyed alongside node ids. */
const UNASSIGNED_GROUP_ID = 'group:unassigned';

const LEVEL_NAME: Record<SalesTeamLevel, { one: string; many: string }> = {
  [SalesTeamLevel.RSM]: { one: 'Regional Sales Manager', many: 'Regional Sales Managers' },
  [SalesTeamLevel.ASM]: { one: 'Area Sales Manager', many: 'Area Sales Managers' },
  [SalesTeamLevel.SO]: { one: 'Sales Officer', many: 'Sales Officers' },
};

const FORBIDDEN_MESSAGE = 'Only admins can change reporting lines';

@Component({
  selector: 'app-sales-team-management',
  standalone: false,
  templateUrl: './sales-team-management.component.html',
  styleUrl: './sales-team-management.component.scss',
})
export class SalesTeamManagementComponent implements OnInit, OnDestroy {
  protected readonly unassignedGroupId: string = UNASSIGNED_GROUP_ID;

  protected loadState: LoadState = 'loading';
  protected team: SalesTeamNode[] = [];
  protected summary: SalesTeamSummary = EMPTY_SALES_TEAM_SUMMARY;
  protected stats: SummaryStat[] = [];
  protected progressSegments: ProgressSegment[] = [];
  protected placedCount: number = 0;

  /** RSMs hang straight off Admin; the Unassigned group follows them as Admin's last branch. */
  protected rsmRows: SalesTeamRow[] = [];
  protected unassignedRows: SalesTeamRow[] = [];
  protected isUnassignedGroupExpanded: boolean = true;

  /** Focus mode: whose branch stays in colour while everything else greys out. */
  protected focusedName: string | null = null;
  protected isUnassignedGroupDimmed: boolean = false;
  /** Whether the focused person sits under Unassigned, for the coloured path through its heading. */
  protected isFocusInUnassignedGroup: boolean = false;
  /** Colour key of Admin's line on the focused path: the level of the focused person's top-level manager. */
  protected focusTopTone: string = '';

  /** Bulk selection: one level at a time, since everyone selected needs the same kind of manager. */
  protected isSelecting: boolean = false;
  protected selectedIds: Set<string> = new Set<string>();
  protected selectionLevel: SalesTeamLevel | null = null;
  /** Rows whose manager is being removed, so their action cannot be pressed twice. */
  protected removingIds: Set<string> = new Set<string>();

  private _index: Map<string, SalesTeamIndexEntry> = new Map();
  private _focusedId: string | null = null;
  private _focus: SalesTeamFocus | null = null;
  private _expandedIds: Set<string> = new Set<string>();
  private _subscription?: Subscription;
  private _pickerRef: NgbModalRef | null = null;

  constructor(
    private _salesTeamApi: SalesTeamManagementApi,
    private _modalService: NgbModal,
    private _snackbarService: SnackbarService
  ) {}

  ngOnInit(): void {
    this.loadHierarchy();
  }

  ngOnDestroy(): void {
    this._subscription?.unsubscribe();
  }

  /** "3 Sales Officers", for the selection bar. */
  protected get selectionLabel(): string {
    const names = this.selectionLevel ? LEVEL_NAME[this.selectionLevel] : { one: 'person', many: 'people' };

    return `${this.selectedIds.size} ${this.selectedIds.size === 1 ? names.one : names.many}`;
  }

  protected loadHierarchy(): void {
    this.loadState = 'loading';
    this._subscription?.unsubscribe();

    // 401 is handled by the auth interceptor (refresh, or logout when that fails).
    this._subscription = this._salesTeamApi.hierarchy().subscribe({
      next: (team: SalesTeamNode[]) => {
        // Start with Unassigned and the RSMs open, so their ASMs are visible.
        this._expandedIds = new Set([
          UNASSIGNED_GROUP_ID,
          ...team.filter(node => isRegionalSalesManager(node) && node.reports.length > 0).map(node => node.id),
        ]);
        this._focusedId = null;
        this._applyTeam(team);

        this.loadState = 'ready';
      },
      error: (error: HttpErrorResponse) => {
        this.loadState = error.status === 403 ? 'forbidden' : 'error';
      },
    });
  }

  protected onToggle(id: string): void {
    if (this._expandedIds.has(id)) {
      this._expandedIds.delete(id);
    } else {
      this._expandedIds.add(id);
    }

    this._rebuildRows();
  }

  /** Clicking the focused person again clears the focus. */
  protected onSelect(id: string): void {
    this._setFocus(this._focusedId === id ? null : id);
  }

  @HostListener('document:keydown.escape')
  protected onClearFocus(): void {
    if (this._focusedId && !this._pickerRef) {
      this._setFocus(null);
    }
  }

  protected onExpandAll(): void {
    this._expandedIds = new Set([UNASSIGNED_GROUP_ID, ...expandableIds(this.team)]);
    this._rebuildRows();
  }

  protected onCollapseAll(): void {
    this._expandedIds = new Set<string>();
    this._rebuildRows();
  }

  // Reporting lines ----------------------------------------------------------------------------

  /** RSMs report to Admin and cannot be given a manager, so they get no action and no checkbox. */
  protected canManage(node: SalesTeamNode): boolean {
    return !!managerLevelFor(node.designation);
  }

  protected hasManager(node: SalesTeamNode): boolean {
    return !!this._index.get(node.id)?.managerId;
  }

  /**
   * Who gets a checkbox in select mode: anyone who can have a manager, and once someone is ticked, only
   * people of that level, since they all need the same kind of manager. Others simply have no checkbox.
   */
  protected canSelect(node: SalesTeamNode): boolean {
    return this.canManage(node) && (!this.selectionLevel || salesTeamLevel(node.designation) === this.selectionLevel);
  }

  protected onStartSelecting(): void {
    this.isSelecting = true;
  }

  protected onStopSelecting(): void {
    this.isSelecting = false;
    this._setSelection(new Set());
  }

  protected onSelectionChange(node: SalesTeamNode, selected: boolean): void {
    const next = new Set(this.selectedIds);

    if (selected) {
      next.add(node.id);
    } else {
      next.delete(node.id);
    }

    this._setSelection(next);
  }

  protected onAssignManager(node: SalesTeamNode): void {
    this._openPicker([node], false);
  }

  protected onBulkAssign(): void {
    const employees = [...this.selectedIds]
      .map(id => this._index.get(id)?.node)
      .filter((node): node is SalesTeamNode => !!node);

    if (employees.length) {
      this._openPicker(employees, true);
    }
  }

  protected onRemoveManager(node: SalesTeamNode): void {
    const managerId = this._index.get(node.id)?.managerId;
    const modalRef = this._modalService.open(RemoveManagerModalComponent, { backdrop: 'static' });
    const dialog = modalRef.componentInstance as RemoveManagerModalComponent;

    dialog.employee = node;
    dialog.managerName = (managerId && this._index.get(managerId)?.node.name) || 'their manager';
    modalRef.closed.subscribe(() => this._removeManager(node));
  }

  private _removeManager(node: SalesTeamNode): void {
    const done = (): void => {
      this.removingIds.delete(node.id);
      this._snackbarService.show(`${node.name} now reports to Admin`, SnackbarAlertStatus.SUCCESS);
      this._reload([node.id]);
    };

    this.removingIds.add(node.id);
    this._salesTeamApi.removeManager(node.id).subscribe({
      next: done,
      error: (error: HttpErrorResponse) => {
        // 404 means they had no manager already: the outcome the admin wanted, so refresh to match.
        if (error.status === 404) {
          done();

          return;
        }

        this.removingIds.delete(node.id);

        if (error.status === 401) {
          return;
        }
        if (error.status === 403) {
          this._snackbarService.show(FORBIDDEN_MESSAGE, SnackbarAlertStatus.ERROR);

          return;
        }
        if (error.status === 0 || error.status >= 500) {
          this._snackbarService
            .show(`Couldn't remove ${node.name}'s manager. Try again.`, SnackbarAlertStatus.ERROR, 8000, 'Retry')
            .onAction()
            .subscribe(() => this._removeManager(node));

          return;
        }

        const parsed = parseReportingLineErrors(error.error, [node.id]);
        const messages = [...parsed.manager, ...parsed.list, ...parsed.other, ...(parsed.byEmployee[node.id] ?? [])];

        this._snackbarService.show(
          messages.join(' ') || `Couldn't remove ${node.name}'s manager.`,
          SnackbarAlertStatus.ERROR,
          6000
        );
      },
    });
  }

  private _openPicker(employees: SalesTeamNode[], bulk: boolean): void {
    const managerIds = new Set(employees.map(employee => this._index.get(employee.id)?.managerId ?? null));
    const picker = (this._pickerRef = this._modalService.open(ManagerPickerModalComponent, {
      backdrop: 'static',
      keyboard: false,
      size: bulk ? 'lg' : undefined,
    }));
    const instance = picker.componentInstance as ManagerPickerModalComponent;

    instance.employees = employees;
    instance.bulk = bulk;
    instance.currentManagerId = managerIds.size === 1 ? [...managerIds][0] : null;
    this._syncPicker();
    instance.init();
    instance.refreshRequested.subscribe(() => this._reload());

    picker.closed.subscribe((result: ManagerPickerResult | undefined) => {
      if (result && 'lines' in result) {
        this._announce(result.lines);
        if (bulk) {
          this.onStopSelecting();
        }
        this._reload(result.lines.map(line => line.employee));
      } else {
        this._reload();
      }
    });
    picker.hidden.subscribe(() => (this._pickerRef = null));
  }

  /** Keeps an open picker's candidates and current-manager names in step with the tree. */
  private _syncPicker(): void {
    const instance = this._pickerRef?.componentInstance as ManagerPickerModalComponent | undefined;

    if (!instance) {
      return;
    }

    instance.candidates = instance.employees[0] ? managerCandidates(this.team, instance.employees[0].designation) : [];
    instance.currentManagerNames = Object.fromEntries(
      instance.employees.map(employee => {
        const managerId = this._index.get(employee.id)?.managerId;

        return [employee.id, managerId ? (this._index.get(managerId)?.node.name ?? 'Admin') : 'Admin'];
      })
    );
  }

  private _announce(lines: ReportingLine[]): void {
    if (!lines.length) {
      return;
    }

    const message =
      lines.length === 1
        ? `${lines[0].employee_name} now reports to ${lines[0].manager_name}`
        : `${lines.length} employees now report to ${lines[0].manager_name}`;

    this._snackbarService.show(message, SnackbarAlertStatus.SUCCESS, 4000);
  }

  /**
   * Refetches the tree without the loading state, so the rows stay in place and the scroll position
   * holds. Expand state and focus carry over; `revealIds` opens whatever is needed to show those people
   * where they now sit.
   */
  private _reload(revealIds: string[] = []): void {
    this._subscription?.unsubscribe();
    this._subscription = this._salesTeamApi.hierarchy().subscribe({
      next: (team: SalesTeamNode[]) => {
        this._applyTeam(team);
        revealIds.forEach(id => this._reveal(id));
        this._rebuildRows();
      },
      error: (error: HttpErrorResponse) => {
        if (error.status === 401) {
          return;
        }

        this._snackbarService
          .show("Couldn't refresh the sales hierarchy.", SnackbarAlertStatus.ERROR, 8000, 'Retry')
          .onAction()
          .subscribe(() => this._reload(revealIds));
      },
    });
  }

  /** Opens the managers above someone, and Unassigned if that is where their branch starts. */
  private _reveal(id: string): void {
    const branch = focusBranch(this.team, id);

    if (!branch) {
      return;
    }

    branch.ancestorIds.forEach(ancestorId => this._expandedIds.add(ancestorId));

    const topNode = this._index.get(branch.ancestorIds[0] ?? id)?.node;

    if (topNode && !isRegionalSalesManager(topNode)) {
      this._expandedIds.add(UNASSIGNED_GROUP_ID);
    }
  }

  private _applyTeam(team: SalesTeamNode[]): void {
    this.team = team;
    this._index = indexSalesTeam(team);
    this.summary = summarizeSalesTeam(team);
    this._buildOverview();

    // Drop anyone who left the tree from the selection and the focus.
    this._setSelection(new Set([...this.selectedIds].filter(id => this._index.has(id))));
    this._syncPicker();
    this._setFocus(this._focusedId && this._index.has(this._focusedId) ? this._focusedId : null);
  }

  private _setSelection(ids: Set<string>): void {
    const first = [...ids][0];

    this.selectedIds = ids;
    this.selectionLevel = first ? salesTeamLevel(this._index.get(first)?.node.designation ?? '') : null;
  }

  private _buildOverview(): void {
    const { totalCount, rsmCount, asmCount, soCount, unassignedCount } = this.summary;
    const percentOf = (count: number): number => (totalCount ? (count / totalCount) * 100 : 0);

    this.placedCount = totalCount - unassignedCount;
    this.stats = [
      { label: 'Sales team', count: totalCount, tone: 'all' },
      { label: 'Regional Sales Managers', count: rsmCount, tone: 'rsm' },
      { label: 'Area Sales Managers', count: asmCount, tone: 'asm' },
      { label: 'Sales Officers', count: soCount, tone: 'so' },
      { label: 'Unassigned', count: unassignedCount, tone: 'unassigned' },
    ];
    // RSMs report to Admin by rule, so all of them count as placed.
    this.progressSegments = [
      { tone: 'rsm', label: 'Regional Sales Managers placed', percent: percentOf(rsmCount) },
      {
        tone: 'asm',
        label: 'Area Sales Managers placed',
        percent: percentOf(asmCount - this.summary.unassignedAsmCount),
      },
      { tone: 'so', label: 'Sales Officers placed', percent: percentOf(soCount - this.summary.unassignedSoCount) },
    ].filter(segment => segment.percent > 0);
  }

  /** Focusing someone also opens their branch, and Unassigned if they sit there, so their team is visible. */
  private _setFocus(id: string | null): void {
    this._focus = id ? focusBranch(this.team, id) : null;
    this._focusedId = this._focus ? id : null;

    const topId = this._focus ? (this._focus.ancestorIds[0] ?? id) : null;
    const topNode = this.team.find(node => node.id === topId);
    const focusedNode = this._focusedId ? this._index.get(this._focusedId)?.node : null;

    this.focusedName = focusedNode?.name ?? null;
    this.isFocusInUnassignedGroup = !!topNode && !isRegionalSalesManager(topNode);
    this.focusTopTone = topNode ? levelTone(topNode) : '';
    this.isUnassignedGroupDimmed = !!topNode && isRegionalSalesManager(topNode);

    if (this._focus && this._focusedId) {
      if (this.isFocusInUnassignedGroup) {
        this._expandedIds.add(UNASSIGNED_GROUP_ID);
      }
      this._focus.ancestorIds.forEach(ancestorId => this._expandedIds.add(ancestorId));
      if (focusedNode?.reports.length) {
        this._expandedIds.add(this._focusedId);
      }
    }

    this._rebuildRows();
  }

  private _applyFocus(rows: SalesTeamRow[]): SalesTeamRow[] {
    if (!this._focus) {
      return rows;
    }

    const focus = this._focus;

    return rows.map(row => ({
      ...row,
      isFocused: row.node.id === this._focusedId,
      isDimmed: !focus.ids.has(row.node.id),
    }));
  }

  private _rebuildRows(): void {
    const rsms = this.team.filter(isRegionalSalesManager);
    const unassigned = this.team.filter(node => !isRegionalSalesManager(node));

    this.isUnassignedGroupExpanded = this._expandedIds.has(UNASSIGNED_GROUP_ID);

    // RSMs sit on Admin's line, which carries on past them to the Unassigned heading; that stretch is on
    // the focused path when the focus sits in Unassigned.
    const rsmFocus = this._focus ? { ids: this._focus.ids, baseGuides: [], baseGuideTones: [] } : null;
    const toUnassigned = { onPath: this.isFocusInUnassignedGroup, tone: this.focusTopTone };
    const unassignedFocus = this._focus ? { ids: this._focus.ids, baseGuides: [false], baseGuideTones: [''] } : null;

    this.rsmRows = this._applyFocus(flattenSalesTeam(rsms, this._expandedIds, [], rsmFocus, toUnassigned));
    this.unassignedRows = this.isUnassignedGroupExpanded
      ? this._applyFocus(flattenSalesTeam(unassigned, this._expandedIds, [false], unassignedFocus))
      : [];
  }
}
